import { useMutation } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

export interface EmployeeCSVRow {
  email: string;
  first_name: string;
  last_name: string;
  department: string;
  job_title: string;
  hire_date: string;
  manager_email?: string;
  salary?: number;
  is_active?: boolean;
  notes?: string;
}

export interface EmployeeCSVValidationError {
  row: number;
  column: string;
  message: string;
}

export interface EmployeeCSVImportResult {
  imported: number;
  errors: number;
  created_employee_ids?: string[];
  skipped_conflicts?: string[];
}

type ConflictMode = 'skip' | 'replace' | 'abort';

function splitCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let insideQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === ',' && !insideQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/^"|"$/g, '');
}

function parseBoolean(value: string): boolean | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes'].includes(normalized)) return true;
  if (['false', '0', 'no'].includes(normalized)) return false;
  return undefined;
}

function parseNumber(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseEmployeeCsv(text: string): EmployeeCSVRow[] {
  const lines = text
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return [];

  const [headerLine, ...dataLines] = lines;
  if (!headerLine) return [];

  const headers = splitCsvLine(headerLine).map(normalizeHeader);

  return dataLines.map((line) => {
    const columns = splitCsvLine(line);
    const row = Object.fromEntries(headers.map((header, index) => [header, columns[index] ?? '']));

    return {
      email: row.email ?? '',
      first_name: row.first_name ?? '',
      last_name: row.last_name ?? '',
      department: row.department ?? '',
      job_title: row.job_title ?? '',
      hire_date: row.hire_date ?? '',
      manager_email: row.manager_email || undefined,
      salary: parseNumber(row.salary ?? ''),
      is_active: parseBoolean(row.is_active ?? ''),
      notes: row.notes || undefined,
    } satisfies EmployeeCSVRow;
  });
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
}

function isFutureDate(value: string): boolean {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed.getTime() > today.getTime();
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value);
}

export function validateEmployeeCSV(rows: EmployeeCSVRow[]): {
  valid: EmployeeCSVRow[];
  errors: EmployeeCSVValidationError[];
} {
  const errors: EmployeeCSVValidationError[] = [];
  const valid: EmployeeCSVRow[] = [];
  const seenEmails = new Set<string>();

  rows.forEach((row, rowIndex) => {
    const currentErrors: EmployeeCSVValidationError[] = [];
    const csvRow = rowIndex + 2;
    const email = row.email.trim().toLowerCase();
    const firstName = row.first_name.trim();
    const lastName = row.last_name.trim();

    if (!email || !isValidEmail(email)) {
      currentErrors.push({ row: csvRow, column: 'email', message: 'invalid_email' });
    } else if (seenEmails.has(email)) {
      currentErrors.push({ row: csvRow, column: 'email', message: 'duplicate_email' });
    } else {
      seenEmails.add(email);
    }

    if (!firstName) {
      currentErrors.push({ row: csvRow, column: 'first_name', message: 'required' });
    }

    if (!lastName) {
      currentErrors.push({ row: csvRow, column: 'last_name', message: 'required' });
    }

    if (row.hire_date && (!isIsoDate(row.hire_date) || isFutureDate(row.hire_date))) {
      currentErrors.push({ row: csvRow, column: 'hire_date', message: 'invalid_date' });
    }

    if (row.salary !== undefined && row.salary <= 0) {
      currentErrors.push({ row: csvRow, column: 'salary', message: 'invalid_salary' });
    }

    if (row.manager_email && !isValidEmail(row.manager_email.trim().toLowerCase())) {
      currentErrors.push({ row: csvRow, column: 'manager_email', message: 'invalid_email' });
    }

    if (currentErrors.length > 0) {
      errors.push(...currentErrors);
      return;
    }

    valid.push({
      ...row,
      email,
      first_name: firstName,
      last_name: lastName,
      department: row.department.trim(),
      job_title: row.job_title.trim(),
      manager_email: row.manager_email?.trim() || undefined,
      notes: row.notes?.trim() || undefined,
    });
  });

  return { valid, errors };
}

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

export function useImportEmployeesCSV(supabase: SupabaseClientLike, tenantId: string) {
  return useMutation({
    mutationFn: async ({
      rows,
      conflictMode,
    }: {
      rows: EmployeeCSVRow[];
      conflictMode?: ConflictMode;
    }): Promise<EmployeeCSVImportResult> => {
      const headers = await getAuthHeaders(supabase);
      const response = await fetch(buildFunctionUrl('people/import'), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-tenant-id': tenantId,
          ...headers,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          rows,
          conflict_mode: conflictMode ?? 'abort',
        }),
      });

      if (!response.ok) {
        const body = (await response
          .json()
          .catch(() => ({ error: { message: response.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? response.statusText);
      }

      return response.json() as Promise<EmployeeCSVImportResult>;
    },
  });
}
